import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useReleases, useSavedSearches, useCreateSavedSearch, useUpdateSavedSearch, useDeleteSavedSearch } from '../hooks'
import { track } from '../utils/track'
import { ReleaseFilters as ReleaseFiltersType } from '../types/release'
import { LoadingState, Pagination, ReleaseToolbar } from '../components/common'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import { useViewSort } from '../context/ViewSortContext'
import { cn } from '../utils'
import styles from './PageStyles.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

const DEFAULT_FILTERS: ReleaseFiltersType = {
  page: 1,
  per_page: 50,
  sort: 'bayesian_rating',
  order: 'desc',
}

function sanitizeReleaseFilters(filters: ReleaseFiltersType): ReleaseFiltersType {
  const { min_elevated_score: _minScore, max_last_sold_days: _maxDays, ...rest } = filters
  return rest
}

// Sort keys that read more naturally ascending.
const SORT_DEFAULT_ASC = new Set(['title', 'released'])

function getReleasesTabKey(): string {
  try {
    const token = localStorage.getItem('stonks_token')
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (payload.exp * 1000 > Date.now() && typeof payload.id === 'number') {
        return `stonks_releases_active_tab:${payload.id}`
      }
    }
  } catch { /* ignore */ }
  return 'stonks_releases_active_tab'
}

export function ReleasesPage() {
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()
  const releasesTabKey = useMemo(getReleasesTabKey, [])

  // Initialize filters from sessionStorage (preserves state across navigation)
  const [filters, setFilters] = useState<ReleaseFiltersType>(() => {
    try {
      const saved = sessionStorage.getItem('stonks_releases_filters')
      if (saved) return sanitizeReleaseFilters(JSON.parse(saved))
    } catch { /* ignore */ }
    return { ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder }
  })
  const [activeTabId, setActiveTabId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(releasesTabKey)
      return saved ? parseInt(saved) : null
    } catch {
      return null
    }
  }) // null = "All Releases"
  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState(() => {
    try {
      const saved = sessionStorage.getItem('stonks_releases_filters')
      if (saved) { const f = JSON.parse(saved); return f.q || '' }
    } catch { /* ignore */ }
    return ''
  })
  const [showNewTabInput, setShowNewTabInput] = useState(false)
  const [newTabName, setNewTabName] = useState('')
  const newTabInputRef = useRef<HTMLInputElement>(null)

  // Skip the auto-save that would otherwise fire when filters are loaded
  // (mount, tab switch) rather than edited by the user — starts true so the
  // initial mount never overwrites a tab with stale/default filters.
  const suppressAutoSave = useRef(true)

  const { data, isLoading, error } = useReleases(filters)
  const { data: savedSearchesData } = useSavedSearches<ReleaseFiltersType>('releases')
  const createSearch = useCreateSavedSearch('releases')
  const updateSearch = useUpdateSavedSearch()
  const deleteSearch = useDeleteSavedSearch('releases')

  const savedSearches = savedSearchesData?.searches || []

  // Persist filters to sessionStorage for navigation preservation
  useEffect(() => {
    try { sessionStorage.setItem('stonks_releases_filters', JSON.stringify(sanitizeReleaseFilters(filters))) } catch { /* ignore */ }
  }, [filters])

  // Sync global sort into filters when on "All Releases" tab
  useEffect(() => {
    if (activeTabId === null) {
      setFilters(prev => ({
        ...prev,
        sort: globalSort,
        order: globalOrder,
      }))
    }
  }, [globalSort, globalOrder, activeTabId])

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    try {
      if (activeTabId !== null) {
        localStorage.setItem(releasesTabKey, activeTabId.toString())
      } else {
        localStorage.removeItem(releasesTabKey)
      }
    } catch {
      // Ignore storage errors
    }
  }, [activeTabId, releasesTabKey])

  // Auto-save the active tab whenever its filters/sort/page change. The tab is
  // a snapshot of digging progress, so we persist sort, filters, and page.
  useEffect(() => {
    if (activeTabId === null) return
    if (suppressAutoSave.current) {
      suppressAutoSave.current = false
      return
    }
    const timeout = setTimeout(() => {
      updateSearch.mutate({ id: activeTabId, updates: { filters: sanitizeReleaseFilters(filters) } })
    }, 500)
    return () => clearTimeout(timeout)
  }, [filters, activeTabId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Focus new tab input when shown
  useEffect(() => {
    if (showNewTabInput && newTabInputRef.current) {
      newTabInputRef.current.focus()
    }
  }, [showNewTabInput])

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setFilters(prev => ({ ...prev, q: value || undefined, page: 1 }))
  }, [])

  // Debounce: auto-search as the user types
  useEffect(() => {
    const timeout = setTimeout(() => {
      const next = search.trim() || undefined
      setFilters(prev => {
        if (prev.q === next) return prev
        if (next) track('search', { q: next, scope: 'releases' })
        return { ...prev, q: next, page: 1 }
      })
    }, 350)
    return () => clearTimeout(timeout)
  }, [search])

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Tab actions
  const handleTabClick = (tabId: number | null) => {
    setActiveTabId(tabId)

    if (tabId === null) {
      suppressAutoSave.current = true
      setFilters({ ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder, q: undefined })
      setSearch('')
    } else {
      const saved = savedSearches.find(s => s.id === tabId)
      if (saved) {
        suppressAutoSave.current = true
        setFilters(sanitizeReleaseFilters(saved.filters))
        setSearch(saved.filters.q || '')
      }
    }
  }

  // Inside a custom tab the sort belongs to the tab (it's part of the saved
  // digging state), not the global default — so editing it writes to filters
  // (which then auto-saves) instead of the shared ViewSort context.
  const tabSortProps = activeTabId !== null ? {
    sort: filters.sort,
    order: filters.order,
    onSortChange: (sort: string) =>
      setFilters(prev => ({ ...prev, sort, order: SORT_DEFAULT_ASC.has(sort) ? 'asc' as const : 'desc' as const, page: 1 })),
    onToggleOrder: () =>
      setFilters(prev => ({ ...prev, order: prev.order === 'desc' ? 'asc' as const : 'desc' as const })),
  } : undefined

  const handleCreateTab = () => {
    if (!newTabName.trim()) return
    createSearch.mutate(
      { name: newTabName.trim(), filters: sanitizeReleaseFilters(filters) },
      {
        onSuccess: (data) => {
          setActiveTabId(data.search.id)
          setNewTabName('')
          setShowNewTabInput(false)
        },
      }
    )
  }

  const handleDeleteTab = (e: React.MouseEvent, tabId: number) => {
    e.stopPropagation()
    deleteSearch.mutate(tabId)
    if (activeTabId === tabId) {
      setActiveTabId(null)
      suppressAutoSave.current = true
      setFilters({ ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder })
      setSearch('')
    }
  }

  const activeFilterCount = (
    (filters.genres?.length ?? 0) +
    (filters.styles?.length ?? 0) +
    (filters.countries?.length ?? 0) +
    (filters.year_from ? 1 : 0) +
    (filters.year_to ? 1 : 0) +
    (filters.min_want ? 1 : 0) +
    (filters.min_median ? 1 : 0) +
    (filters.max_median ? 1 : 0)
  )

  if (error) {
    return (
      <div className={styles.error}>
        <i className="bi bi-exclamation-triangle"></i>
        <p>Failed to load releases</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>
          Releases
          {data?.pagination?.total !== undefined && (
            <span className={styles.countBadge}>{data.pagination.total.toLocaleString()}</span>
          )}
        </h1>
      </div>

      {/* Saved Search Tabs */}
      <div className={releaseStyles.tabBar}>
        <button
          className={cn(releaseStyles.tab, activeTabId === null && releaseStyles.tabActive)}
          onClick={() => handleTabClick(null)}
        >
          All Releases
        </button>
        {savedSearches.map(s => (
          <button
            key={s.id}
            className={cn(releaseStyles.tab, activeTabId === s.id && releaseStyles.tabActive)}
            onClick={() => handleTabClick(s.id)}
          >
            {s.name}
            <span
              className={releaseStyles.tabClose}
              onClick={(e) => handleDeleteTab(e, s.id)}
            >
              <i className="bi bi-x"></i>
            </span>
          </button>
        ))}
        {showNewTabInput ? (
          <div className={releaseStyles.newTabInput}>
            <input
              ref={newTabInputRef}
              type="text"
              placeholder="Tab name..."
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateTab()
                if (e.key === 'Escape') { setShowNewTabInput(false); setNewTabName('') }
              }}
            />
            <button onClick={handleCreateTab} className={releaseStyles.newTabConfirm}>
              <i className="bi bi-check"></i>
            </button>
            <button onClick={() => { setShowNewTabInput(false); setNewTabName('') }} className={releaseStyles.newTabCancel}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        ) : (
          <button
            className={releaseStyles.tabAdd}
            onClick={() => setShowNewTabInput(true)}
            title="Save current search as a new tab"
            aria-label="Save current search as a new tab"
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        )}
      </div>

      <ReleaseToolbar
        search={search}
        onSearchChange={setSearch}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search tracks, artist ..."
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFiltersChange={update => setFilters(prev => ({ ...prev, ...update }))}
        activeFilterCount={activeFilterCount}
        viewSortProps={tabSortProps}
      />

      {showFilters && (
        <ReleaseFilters filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} />
      )}

      {isLoading ? (
        <LoadingState message="Loading releases..." />
      ) : (
        <div className={releaseStyles.releasesLayout}>
          <div className={releaseStyles.tableContainer}>
            <ReleaseListView
              releases={data?.releases ?? []}
              viewMode={viewMode}
              emptyHint="Try adjusting your filters"
            />

            {data && data.pagination && (
              <Pagination
                page={data.pagination.page}
                totalPages={data.pagination.total_pages}
                hasNext={data.pagination.page < data.pagination.total_pages}
                hasPrev={data.pagination.page > 1}
                onPageChange={handlePageChange}
                perPage={filters.per_page}
                onPerPageChange={(per_page) => setFilters({ ...filters, per_page, page: 1 })}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
